import { Injectable, inject, Signal } from '@angular/core';
import {
  injectQuery,
  injectMutation,
  QueryClient,
  CreateQueryResult,
  CreateMutationResult,
} from '@tanstack/angular-query-experimental';
import { lastValueFrom } from 'rxjs';

import { ServiceSettingsHttpService } from '@http-services/ServiceSettingsHttpService';
import { GetListRequest, GetDetailRequest } from '@rest/ViewSetHttpService';
import { serviceSettingsKeys } from './service-settings-keys';
import { userKeys } from '@query/userQueryService/user-keys';
import {
  ServiceSettings,
  ServiceSettingsList,
  CreateServiceSettingsDto,
  UpdateServiceSettingsPayload,
  SendTestEmailDto,
  UpdateUISettingsDto,
  SendTestEmailResponse,
  UpdateUISettingsResponse,
} from './service-settings-types';
import { createSafeParams } from '@query/utils/angular-query.utils';

/**
 * Angular Query wrapper for ServiceSettingsHttpService
 * Provides caching, state management, and automatic query invalidation
 */
@Injectable({
  providedIn: 'root',
})
export class ServiceSettingsQueryService {
  private readonly serviceSettingsService = inject(ServiceSettingsHttpService);
  private readonly queryClient = inject(QueryClient);

  /**
   * List service settings with Angular Query
   * @param params Query parameters
   * @param enabled Optional signal to control when the query should be enabled
   * @returns Query result with loading, error, and data states
   */
  getServiceSettings({
    params = {},
    enabled,
  }: {
    params?: GetListRequest;
    enabled?: Signal<boolean>;
  } = {}): CreateQueryResult<ServiceSettingsList, Error> {
    return injectQuery(() => {
      const isEnabled = enabled ? enabled() : true;
      const safeParams = createSafeParams(params);

      return {
        queryKey: serviceSettingsKeys.list(safeParams),
        queryFn: () =>
          lastValueFrom(
            this.serviceSettingsService.list(safeParams)
          ) as Promise<ServiceSettingsList>,
        enabled: isEnabled,
        staleTime: 30 * 60 * 1000,
      };
    });
  }

  /**
   * Get service settings detail
   * @param uuid Signal containing the service settings UUID
   * @param params Query parameters
   * @param enabled Optional signal to control when the query should be enabled
   * @returns Query result with loading, error, and data states
   */
  getServiceSettingsDetail({
    uuid,
    params = {},
    enabled,
  }: {
    uuid: Signal<string | undefined>;
    params?: GetDetailRequest;
    enabled?: Signal<boolean>;
  }): CreateQueryResult<ServiceSettings, Error> {
    return injectQuery(() => {
      const settingsUuid = uuid();
      const isEnabled = enabled ? enabled() : true;
      const shouldRun = isEnabled && !!settingsUuid;
      const safeParams = createSafeParams(params);

      return {
        queryKey: serviceSettingsKeys.detail(settingsUuid || '', params),
        queryFn: () =>
          lastValueFrom(
            this.serviceSettingsService.detail(settingsUuid!, safeParams)
          ) as Promise<ServiceSettings>,
        enabled: shouldRun,
      };
    });
  }

  /**
   * Create new service settings
   * @returns Mutation result with mutate function
   */
  createServiceSettings(): CreateMutationResult<ServiceSettings, Error, CreateServiceSettingsDto> {
    return injectMutation(() => ({
      mutationFn: data => {
        return lastValueFrom(this.serviceSettingsService.create(data)) as Promise<ServiceSettings>;
      },
      onSuccess: () => {
        return this.queryClient.invalidateQueries({
          queryKey: serviceSettingsKeys.invalidateLists(),
        });
      },
    }));
  }

  /**
   * Update existing service settings
   * @returns Mutation result with mutate function
   */
  updateServiceSettings(): CreateMutationResult<
    ServiceSettings,
    Error,
    UpdateServiceSettingsPayload
  > {
    return injectMutation(() => ({
      mutationFn: ({ uuid, data }) => {
        return lastValueFrom(this.serviceSettingsService.update(uuid, data));
      },
      onSuccess: (_, variables) => {
        return Promise.all([
          this.queryClient.invalidateQueries({
            queryKey: serviceSettingsKeys.detailById(variables.uuid),
          }),
          this.queryClient.invalidateQueries({ queryKey: serviceSettingsKeys.lists() }),
        ]);
      },
    }));
  }

  /**
   * Delete service settings
   * @returns Mutation result with mutate function
   */
  deleteServiceSettings(): CreateMutationResult<ServiceSettings, Error, string> {
    return injectMutation(() => ({
      mutationFn: (uuid: string) => lastValueFrom(this.serviceSettingsService.delete(uuid)),
      onSuccess: () => {
        return this.queryClient.invalidateQueries({
          queryKey: serviceSettingsKeys.invalidateAll(),
        });
      },
    }));
  }

  /**
   * Send test email
   * @returns Mutation result with mutate function
   */
  sendTestEmail(): CreateMutationResult<SendTestEmailResponse, Error, SendTestEmailDto> {
    return injectMutation(() => ({
      mutationFn: ({ email }) => {
        return lastValueFrom(
          this.serviceSettingsService.sendTestEmail(email)
        ) as Promise<SendTestEmailResponse>;
      },
    }));
  }

  /**
   * Update UI settings
   * @returns Mutation result with mutate function
   */
  updateUISettings(): CreateMutationResult<UpdateUISettingsResponse, Error, UpdateUISettingsDto> {
    return injectMutation(() => ({
      mutationFn: ({ settingId, settingValue }) => {
        return lastValueFrom(
          this.serviceSettingsService.updateUISettings(settingId, settingValue)
        ) as Promise<UpdateUISettingsResponse>;
      },
      onSuccess: () => {
        // Invalidate both service settings and user cache
        // because the response includes user data with isOnboardingComplete
        const promises = [
          this.queryClient.invalidateQueries({
            queryKey: serviceSettingsKeys.invalidateAll(),
          }),
          this.queryClient.invalidateQueries({
            queryKey: userKeys.invalidateAll(),
            refetchType: 'active',
          }),
        ];
        return Promise.all(promises);
      },
    }));
  }
}
